import React, { useEffect, useMemo, useState } from "react";
import { api, formatDate, todayISODate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, CalendarDays, ChevronLeft, ChevronRight, Phone } from "lucide-react";
import { toast } from "sonner";

const SERVICE_TYPES = ["Boya", "Kaporta", "Bakım", "Ekspertiz", "Diğer"];
const STATUSES = ["Beklemede", "Geldi", "İptal"];
const STATUS_COLOR = {
  "Beklemede": "bg-amber-50 text-amber-800 border-amber-200",
  "Geldi": "bg-emerald-50 text-emerald-800 border-emerald-200",
  "İptal": "bg-slate-100 text-slate-500 border-slate-200",
};

const MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
                "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

const empty = {
  customer_name: "", customer_phone: "", plate: "", car_model: "",
  date: `${todayISODate()}T10:00`,
  service_type: "Boya", status: "Beklemede", note: "",
};

// Ayın günlerini + öncesindeki-sonrasındaki gün hücrelerini üret (Pzt başlangıç)
function buildMonthGrid(year, monthIndex) {
  const first = new Date(year, monthIndex, 1);
  // Pzt=1..Paz=7 için ofset (Pzt başlangıç ⇒ Paz 0 idi, biz Pzt 0 istiyoruz)
  const jsDay = first.getDay(); // 0=Sun,1=Mon..6=Sat
  const offset = (jsDay + 6) % 7; // Pzt=0
  const days = [];
  for (let i = 0; i < offset; i++) days.push(null);
  const lastDate = new Date(year, monthIndex + 1, 0).getDate();
  for (let d = 1; d <= lastDate; d++) {
    days.push(new Date(year, monthIndex, d));
  }
  while (days.length % 7 !== 0) days.push(null);
  return days;
}

export default function Appointments() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [deleteId, setDeleteId] = useState(null);

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [monthIndex, setMonthIndex] = useState(now.getMonth());

  const load = async () => setItems((await api.get("/appointments")).data);
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const openNew = (dateISO) => {
    setEditing(null);
    setForm({ ...empty, date: dateISO ? `${dateISO}T10:00` : empty.date });
    setOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      customer_name: a.customer_name || "",
      customer_phone: a.customer_phone || "",
      plate: a.plate || "",
      car_model: a.car_model || "",
      date: (a.date || "").slice(0, 16),
      service_type: a.service_type || "Boya",
      status: a.status || "Beklemede",
      note: a.note || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.customer_name.trim() && !form.plate.trim()) {
      toast.error("Müşteri adı veya plaka gerekli"); return;
    }
    if (!form.date) { toast.error("Tarih girin"); return; }
    const payload = { ...form };
    try {
      if (editing) { await api.put(`/appointments/${editing.id}`, payload); toast.success("Güncellendi"); }
      else { await api.post("/appointments", payload); toast.success("Randevu eklendi"); }
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const doDelete = async () => {
    try { await api.delete(`/appointments/${deleteId}`); toast.success("Silindi"); setDeleteId(null); load(); }
    catch { toast.error("Silinemedi"); }
  };

  const monthKey = `${year}-${String(monthIndex + 1).padStart(2, "0")}`;

  const monthItems = useMemo(() => items.filter((a) => (a.date || "").startsWith(monthKey)), [items, monthKey]);
  const byDay = useMemo(() => {
    const map = {};
    for (const a of monthItems) {
      const day = (a.date || "").slice(0, 10);
      if (!map[day]) map[day] = [];
      map[day].push(a);
    }
    return map;
  }, [monthItems]);

  const grid = buildMonthGrid(year, monthIndex);

  const shiftMonth = (delta) => {
    let m = monthIndex + delta, y = year;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setMonthIndex(m); setYear(y);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Randevular</h1>
          <p className="text-sm text-slate-500">Müşteri randevularını aylık takvim üzerinde yönet</p>
        </div>
        <Button data-testid="add-appointment-btn" onClick={() => openNew()} className="bg-slate-900 hover:bg-slate-800 text-white">
          <Plus className="w-4 h-4 mr-2" /> Yeni Randevu
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
        <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
          <Button variant="outline" size="sm" onClick={() => shiftMonth(-1)} data-testid="month-prev-btn">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="text-center">
            <p className="font-display text-lg font-bold text-slate-900">{MONTHS[monthIndex]} {year}</p>
            <p className="text-xs text-slate-500">{monthItems.length} randevu</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => shiftMonth(1)} data-testid="month-next-btn">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Haftalık başlık */}
        <div className="grid grid-cols-7 border-b border-slate-200 bg-white">
          {["Pzt", "Sal", "Çrş", "Prş", "Cum", "Cmt", "Paz"].map((d) => (
            <div key={d} className="text-center text-[10px] uppercase tracking-widest font-bold text-slate-500 py-2">{d}</div>
          ))}
        </div>

        {/* Gün hücreleri */}
        <div className="grid grid-cols-7 border-l border-slate-100">
          {grid.map((d, i) => {
            const cellKey = d ? d.toISOString().slice(0, 10) : `empty-${i}`;
            if (!d) return <div key={cellKey} className="border-r border-b border-slate-100 bg-slate-50/40 min-h-[100px]"></div>;
            const iso = cellKey;
            const list = byDay[iso] || [];
            const isToday = iso === todayISODate();
            return (
              <div key={cellKey} className={`border-r border-b border-slate-100 p-1.5 min-h-[100px] flex flex-col ${isToday ? "bg-blue-50/30" : ""}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-bold ${isToday ? "text-blue-700" : "text-slate-700"}`}>{d.getDate()}</span>
                  <button
                    onClick={() => openNew(iso)}
                    data-testid={`add-day-${iso}`}
                    className="w-4 h-4 rounded-full hover:bg-slate-200 text-slate-400 hover:text-slate-700 text-[10px] leading-none flex items-center justify-center"
                    title="Yeni randevu"
                  >
                    +
                  </button>
                </div>
                <div className="space-y-1 overflow-y-auto flex-1">
                  {list.map((a) => (
                    <button
                      key={a.id}
                      data-testid={`appointment-cell-${a.id}`}
                      onClick={() => openEdit(a)}
                      className={`w-full text-left px-1.5 py-1 rounded border text-[10px] leading-tight ${STATUS_COLOR[a.status] || ""} hover:brightness-95`}
                    >
                      <div className="font-mono-num font-bold">{(a.date || "").slice(11, 16)}</div>
                      <div className="font-semibold truncate">{a.customer_name || a.plate || "-"}</div>
                      {a.plate && <div className="text-[9px] opacity-80 font-mono-num">{a.plate}</div>}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Aylık liste */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
          <h3 className="font-display text-base font-bold text-slate-900">{MONTHS[monthIndex]} {year} — Detaylı Liste</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-white text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100">
            <tr>
              <th className="text-left px-4 py-2 font-semibold">Tarih / Saat</th>
              <th className="text-left px-4 py-2 font-semibold">Müşteri</th>
              <th className="text-left px-4 py-2 font-semibold">Plaka / Model</th>
              <th className="text-left px-4 py-2 font-semibold">Servis</th>
              <th className="text-left px-4 py-2 font-semibold">Telefon</th>
              <th className="text-left px-4 py-2 font-semibold">Durum</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {monthItems.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500" data-testid="appointments-empty">Bu ayda randevu yok.</td></tr>
            )}
            {monthItems.map((a) => (
              <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`appointment-row-${a.id}`}>
                <td className="px-4 py-3 text-slate-700 whitespace-nowrap font-mono-num">
                  {formatDate(a.date)} · {(a.date || "").slice(11, 16)}
                </td>
                <td className="px-4 py-3 font-semibold text-slate-900">{a.customer_name || "-"}</td>
                <td className="px-4 py-3">
                  {a.plate && <span className="inline-block px-1.5 py-0.5 bg-slate-100 rounded font-mono-num text-[10px] font-bold text-slate-700">{a.plate}</span>}
                  {a.car_model && <div className="text-[11px] text-slate-500 mt-0.5">{a.car_model}</div>}
                </td>
                <td className="px-4 py-3 text-slate-700">{a.service_type}</td>
                <td className="px-4 py-3 text-slate-700 font-mono-num text-xs">
                  {a.customer_phone ? (
                    <a href={`tel:${a.customer_phone}`} className="inline-flex items-center gap-1 hover:text-blue-700">
                      <Phone className="w-3 h-3" /> {a.customer_phone}
                    </a>
                  ) : "-"}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold border ${STATUS_COLOR[a.status] || ""}`}>
                    {a.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(a)} className="h-8 w-8" data-testid={`edit-appointment-${a.id}`}>
                      <Pencil className="w-4 h-4 text-slate-600" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setDeleteId(a.id)} className="h-8 w-8 hover:bg-rose-50" data-testid={`delete-appointment-${a.id}`}>
                      <Trash2 className="w-4 h-4 text-rose-600" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Form Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="appointment-dialog" className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-slate-700" />
              {editing ? "Randevuyu Düzenle" : "Yeni Randevu"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Müşteri Adı</Label>
                <Input data-testid="appointment-customer-input" value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
              </div>
              <div>
                <Label>Telefon</Label>
                <Input data-testid="appointment-phone-input" value={form.customer_phone}
                  onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Plaka</Label>
                <Input data-testid="appointment-plate-input" value={form.plate}
                  onChange={(e) => setForm({ ...form, plate: e.target.value })} className="uppercase font-mono-num" />
              </div>
              <div>
                <Label>Araç Modeli</Label>
                <Input data-testid="appointment-model-input" value={form.car_model}
                  onChange={(e) => setForm({ ...form, car_model: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Randevu Tarihi & Saati</Label>
                <Input data-testid="appointment-date-input" type="datetime-local" value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              <div>
                <Label>Servis Türü</Label>
                <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                  <SelectTrigger data-testid="appointment-service-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SERVICE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Durum</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="appointment-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Not</Label>
              <Input data-testid="appointment-note-input" value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button data-testid="appointment-save-btn" onClick={save} className="bg-slate-900 hover:bg-slate-800">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Randevuyu sil?</AlertDialogTitle>
            <AlertDialogDescription>Bu işlem geri alınamaz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-appointment" onClick={doDelete} className="bg-rose-600 hover:bg-rose-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
